import * as React from "react";
import { request } from "../../services/cpln";
import { Button, Dropdown, Input, notification } from "antd";
import { Loader, Search } from "react-feather";
import { Audit } from "../../pages/auditTrail/types";
import { getAuditTrailPresetsNew, getDefaultAuditTrailTimeNew, resolveSubjectName } from "../../pages/auditTrail/utils";
import { AuditTrailDiffView } from "../../pages/auditTrail/diffView";
import { ViewModal } from "../modals/viewModal";
import { ScrollParams } from "react-virtualized";
import { AuditGridWrapper } from "./auditGridWrapper";
import { isAuditDiffable, stringToHash } from "../../services/utils";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import jsYaml from "js-yaml";
import {
  DateRangePicker,
  resolveExpressionPair,
  WithTimezone,
  parseDateRangeFromQueryObject,
  buildDateRangeQueryObject,
  timezoneToUrlValue,
  urlValueToTimezone,
} from "../../newcomponents/datepicker";
import { DownloadOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { setUseSplitViewComparison } from "../../store/uiDataSlice";
import { selectDefaultValue, selectLabel, selectValue, setValue as setTimezoneValue } from "../../store/timezoneSlice";
import { getDefaultKindSelectState, KindSelect, KindSelectState } from "../antd/kindselect/KindSelect";
import { parseLink } from "../../utils/linkParser/linkParser";

interface Props {
  kind: string;
  itemName: string;
  resourceId: string;
  version: number;
  refetch?: any;
  selfLink: string;
}
export const AuditTrail: React.FC<Props> = ({ kind, itemName, resourceId, version, refetch, selfLink }) => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);
  const useSplitView = useAppSelector((state) => state.uiData.useSplitViewComparison);
  const timezoneValue = useAppSelector(selectValue);
  const timezoneLabel = useAppSelector(selectLabel);
  const localTimezone = useAppSelector(selectDefaultValue);

  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL params including timezone
  const queryObject = React.useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    // Only parse on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { subject, audit, audits: auditsQs, splitView, viewDiff: viewDiffQS } = queryObject;

  // Parse date range and timezone from URL using utilities
  const urlDateParams = React.useMemo(
    () =>
      parseDateRangeFromQueryObject(queryObject, {
        defaultFrom: null,
        defaultTo: null,
        defaultTimezone: timezoneToUrlValue(timezoneValue, localTimezone),
      }),
    // Only compute on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const urlFrom = urlDateParams.from;
  const urlTo = urlDateParams.to;
  const urlTimezone = urlDateParams.timezone;
  const [viewDiff, setViewDiff] = React.useState(false);
  const [subjectInput, setSubjectInput] = React.useState("");
  const getDefaultAuditContextState = () =>
    getDefaultKindSelectState({
      link: `//auditctx/cpln`,
      value: "cpln",
    });
  const [auditContextValue, setAuditContextValue] = React.useState(getDefaultAuditContextState().value);
  const [auditContextState, setAuditContextState] = React.useState<KindSelectState>(getDefaultAuditContextState());

  const [fromNew, setFromNew] = React.useState<string | null>(null);
  const [toNew, setToNew] = React.useState<string | null>(null);

  const [isUrlStateProcessed, setIsUrlStateProcessed] = React.useState(false);
  const [queried, setQueried] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [audits, setAudits] = React.useState<Audit[]>([]);
  const [selectedAudits, setSelectedAudits] = React.useState<string[]>([]);
  const [showDetailsOf, setShowDetailsOf] = React.useState<null | string>(null);
  const nextLinkRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (isUrlStateProcessed) {
      fetchData();
    }
  }, [isUrlStateProcessed]);

  React.useEffect(() => {
    if (queried) {
      if (viewDiffQS) {
        setViewDiff(viewDiffQS.toString() === "true" ? true : false);
      }
      if (typeof auditsQs === "string") {
        const selectedAuditsQSArray = auditsQs.split(",").map((a) => Number(a));
        const auditsIds = audits.map((a) => a.id);
        const matchedIds = auditsIds.filter((id) => selectedAuditsQSArray.includes(stringToHash(id)));
        setSelectedAudits(matchedIds);
      }
    }
  }, [queried]);

  React.useEffect(() => {
    if (subject) {
      setSubjectInput(subject.toString());
    }
    if (splitView) {
      dispatch(setUseSplitViewComparison(splitView === "true" ? true : false));
    }
    if (audit) {
      const value = audit.toString();
      setAuditContextState((state) => ({ ...state, value: value, name: value, link: `//auditctx/${value}` }));
    }

    // Initialize from/to from URL params
    if (urlFrom) {
      setFromNew(urlFrom);
    } else {
      setFromNew(getDefaultAuditTrailTimeNew());
    }
    if (urlTo) {
      setToNew(urlTo);
    }

    // Initialize timezone from URL if present
    if (urlTimezone) {
      const resolvedTimezone = urlValueToTimezone(urlTimezone, localTimezone);
      if (resolvedTimezone !== timezoneValue) {
        dispatch(setTimezoneValue(resolvedTimezone));
      }
    }

    setIsUrlStateProcessed(true);
  }, []);

  React.useEffect(() => {
    if (!queried || !isUrlStateProcessed) {
      return;
    }

    // Build base query object
    const baseQsObject: Record<string, unknown> = {};
    if (subjectInput) {
      baseQsObject.subject = subjectInput;
    }
    const auditName = parseLink(auditContextState.link, { useInputCtx: true }).name;
    if (auditName) {
      baseQsObject.audit = auditName;
    }

    // Build query object with date range and timezone
    const qsObject = buildDateRangeQueryObject(
      {
        from: fromNew,
        to: toNew,
        timezone: timezoneToUrlValue(timezoneValue, localTimezone),
      },
      baseQsObject,
      { omitTimezoneWhenDefault: "UTC" }, // Only include tz param when not UTC
    );

    const queryString = qs.stringify(qsObject, { arrayFormat: "comma", encode: false });
    navigate({ search: `?${queryString}` });
  }, [
    subjectInput,
    auditContextState.link,
    fromNew,
    toNew,
    timezoneValue,
    localTimezone,
    useSplitView,
    selectedAudits,
    viewDiff,
    isLoading,
  ]);

  function onScroll_Items(params: ScrollParams) {
    if (isLoading) {
      return;
    }
    if (!nextLinkRef.current) {
      return;
    }
    const { scrollHeight, scrollTop, clientHeight } = params;
    const val = scrollHeight - (scrollTop + clientHeight);
    if (val < 1) {
      fetchData();
    }
  }

  function onQuery() {
    nextLinkRef.current = undefined;
    setAudits([]);
    fetchData();
  }

  async function fetchData() {
    try {
      setIsLoading(true);
      let url = `/audit/org/${org}`;
      if (nextLinkRef.current) {
        url = nextLinkRef.current;
      } else {
        let params: string[] = [`resourceType=${kind}`];
        url += `/resource/id/${resourceId}`;
        if (subjectInput.length > 0) {
          params.push(`subjectName=${resolveSubjectName(subjectInput, org)}`);
        }
        const auditName = parseLink(auditContextState.link, { useInputCtx: true }).name;
        if (auditName) {
          params.push(`contextName=${auditName}`);
        }
        // Resolve from/to together to handle start/end cross-references
        const resolved = resolveExpressionPair(fromNew, toNew, timezoneValue);
        if (resolved.from) {
          params.push(`fromEvent=${resolved.from.toISOString()}`);
        }
        if (resolved.to) {
          params.push(`toEvent=${resolved.to.toISOString()}`);
        }
        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }
      }
      const { data } = await request({ service: "audit", url });
      if (nextLinkRef.current) {
        setAudits([...audits, ...data.items]);
      } else {
        setAudits(data.items);
      }
      const _nextLink = data.links.find((l: any) => l.rel === "next")?.href;
      if (data.items.length === 100) {
        nextLinkRef.current = _nextLink;
      } else {
        nextLinkRef.current = undefined;
      }
      setQueried(true);
    } catch (e) {
      const isUnathorized = e.response?.status === 403;
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      if (isUnathorized) errorMessage = "Unauthorized";
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  function onClear() {
    setSubjectInput("");
    // TODO default state?
    setAuditContextState(getDefaultAuditContextState());
    setFromNew(getDefaultAuditTrailTimeNew());
    setToNew(null);
  }

  async function downloadAudits(format: "json" | "yaml") {
    try {
      let blob = "";

      if (format === "json") {
        blob = JSON.stringify(audits, null, 2);
      } else if (format === "yaml") {
        blob = jsYaml.dump(audits, { indent: 2, noRefs: true });
      }

      autoDownloadWithElementA(blob, format);
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  function autoDownloadWithElementA(blobData: any, format: "json" | "yaml") {
    const file = new Blob([blobData], { type: `text/${format}` });
    const href = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.style.display = "none";
    a.classList.add("cpln-temp-a");
    a.download = `${kind}-${itemName}-audits-from-${fromNew}-to${toNew}.${format}`;
    a.href = href;
    a.click();
  }

  if (viewDiff) {
    return (
      <AuditTrailDiffView
        audits={
          selectedAudits.length > 0
            ? audits.filter((a) => a.id && selectedAudits.includes(a.id))
            : audits.filter((a) => isAuditDiffable(a))
        }
        onClose={() => setViewDiff(false)}
        refetch={refetch}
        itemVersion={version}
        selfLink={selfLink}
      />
    );
  }

  return (
    <>
      <div className="mb-2 text-2xl font-medium">Audit Trail</div>
      <div className="flex items-center mb-2">
        <KindSelect
          placeholder="Any Context"
          className="mr-2"
          style={{ width: 150 }}
          kind={"auditctx"}
          optionValue="name"
          value={auditContextValue}
          onChange={setAuditContextValue}
          onStateChange={setAuditContextState}
        />
        <Input
          className="flex-grow basis-0 mr-2"
          value={subjectInput}
          onChange={(e) => setSubjectInput(e.target.value)}
          placeholder={"Subject (email or service account name)"}
        />
        <div className="flex items-center" style={{ width: 170 }}>
          <Button
            type={"primary"}
            style={{ width: isLoading || audits.length < 1 ? 170 : 120 }}
            onClick={onQuery}
            disabled={isLoading}
            loading={isLoading}
          >
            Query
          </Button>
          {isLoading || audits.length < 1 ? null : (
            <Dropdown
              trigger={["click"]}
              menu={{
                onClick: ({ key }: any) => {
                  switch (key) {
                    case "export-json":
                      downloadAudits("json");
                      break;
                    case "export-yaml":
                      downloadAudits("yaml");
                      break;
                    default:
                      break;
                  }
                },
                items: [
                  { key: "export-json", label: "JSON" },
                  { key: "export-yaml", label: "YAML" },
                ],
              }}
            >
              <Button style={{ width: 45, marginLeft: 5 }} type={"default"} icon={<DownloadOutlined />} />
            </Dropdown>
          )}
        </div>
      </div>
      <div className="flex items-center mb-4">
        <span className="mr-2">Time Range ({timezoneLabel})</span>
        <WithTimezone>
          <DateRangePicker
            presets={getAuditTrailPresetsNew()}
            allowClear={[false, true]}
            placeholder={["From", "To"]}
            granularity="second"
            from={fromNew}
            setFrom={setFromNew}
            to={toNew}
            setTo={setToNew}
          />
        </WithTimezone>
        <Button
          type="default"
          style={{ width: 200 }}
          className="ml-2"
          onClick={() => setViewDiff(true)}
          disabled={
            isLoading ||
            (selectedAudits.length > 0 && selectedAudits.length < 2) ||
            audits.filter(isAuditDiffable).length < 2
          }
        >
          View Diff {selectedAudits.length > 1 ? "For Selected" : "For All"}
        </Button>
        <div className="flex-grow" />
        <Button type="default" style={{ width: 150 }} onClick={() => onClear()} disabled={isLoading}>
          Clear All Filters
        </Button>
      </div>
      {isLoading && !nextLinkRef.current && (
        <div className="flex flex-col items-center p-4 border" style={{ borderRadius: 6 }}>
          <Loader className="mb-2 animate-spin" size={16} />
          <p className="text-center text-2xl mt-2">Loading Audit Events</p>
        </div>
      )}
      {audits.length < 1 && queried && !isLoading ? (
        <div className="flex flex-col items-center p-4 border">
          <Search size={16} />
          <p className="text-center text-2xl mt-2">No Audit Events Found</p>
        </div>
      ) : null}
      <AuditGridWrapper
        audits={audits}
        emptySpaceValue={478}
        onDetail={(id) => setShowDetailsOf(id)}
        onScroll={onScroll_Items}
        queried={queried}
        diffSupport={{ selectedAudits, setSelectedAudits }}
      />
      {showDetailsOf ? (
        <ViewModal
          object={audits.find((a) => a.id === showDetailsOf)}
          title={"Audit Log"}
          onClose={() => setShowDetailsOf(null)}
          visible={!!showDetailsOf}
          filename={`audit-${audits.find((a) => a.id === showDetailsOf)?.id}`}
          applyAudit={
            version === audits.find((a) => a.id === showDetailsOf)?.resource?.data?.version
              ? undefined
              : {
                  title: `Apply version ${audits.find((a) => a.id === showDetailsOf)?.resource?.data?.version}`,
                  async onConfirm() {
                    const data = JSON.parse(JSON.stringify(audits.find((a) => a.id === showDetailsOf)!.resource.data));

                    delete data.id;
                    delete data.version;
                    delete data.links;
                    delete data.created;
                    delete data.lastModified;
                    delete data.status;

                    try {
                      await request({ method: "patch", url: selfLink, body: data });
                      if (refetch) {
                        refetch();
                      }
                      notification.success({ message: "Success" });
                    } catch (e) {
                      let errorMessage = e?.response?.data?.message;
                      if (!errorMessage) errorMessage = e.message;
                      notification.warning({ message: "Failed to Apply Version", description: errorMessage });
                    }
                  },
                }
          }
        />
      ) : null}
    </>
  );
};
